import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { toast } from 'react-toastify';
import * as api from './api';
import type { CropFormValues, ListCropsParams } from './schema';

const ROOT_KEY = 'crops';
const listKey = (params: ListCropsParams) =>
  [ROOT_KEY, 'list', params] as const;
const detailKey = (id: string) => [ROOT_KEY, 'detail', id] as const;
const optionsKey = [ROOT_KEY, 'options'] as const;

function describeError(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as
      | { message?: string | string[]; error?: string }
      | undefined;
    const msg = data?.message;
    if (Array.isArray(msg)) return msg.join(', ');
    if (typeof msg === 'string' && msg.length > 0) return msg;
    if (data?.error) return data.error;
  }
  return fallback;
}

export function useCrops(params: ListCropsParams) {
  return useQuery({
    queryKey: listKey(params),
    queryFn: () => api.listCrops(params),
    placeholderData: (prev) => prev,
  });
}

export function useCrop(id: string | null) {
  return useQuery({
    queryKey: detailKey(id ?? ''),
    queryFn: () => api.getCrop(id as string),
    enabled: !!id,
  });
}

export function useCropOptions() {
  return useQuery({
    queryKey: optionsKey,
    queryFn: () => api.getCropOptions(),
    staleTime: 60 * 60 * 1000,
  });
}

export function useCreateCrop() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: CropFormValues) => api.createCrop(values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY, 'list'] });
      queryClient.invalidateQueries({ queryKey: optionsKey });
      toast.success('Crop created');
    },
    onError: (err) => toast.error(describeError(err, 'Failed to create crop')),
  });
}

export function useUpdateCrop() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, values }: { id: string; values: CropFormValues }) =>
      api.updateCrop(id, values),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY, 'list'] });
      queryClient.invalidateQueries({ queryKey: detailKey(updated.id) });
      queryClient.invalidateQueries({ queryKey: optionsKey });
      toast.success('Crop updated');
    },
    onError: (err) => toast.error(describeError(err, 'Failed to update crop')),
  });
}
